import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import DepthMasonry from './effects/DepthMasonry';
import EditorialSpreads from './effects/EditorialSpreads';
import CinematicView from './CinematicView';
import PerfHud from './PerfHud';
import { useElementWidth } from '../../lib/gallery/useElementWidth';
import { DEFAULT_LAYOUT_OPTIONS, type DateOrder, type Grouping } from '../../lib/gallery/spreads';
import type { PhotoItem } from '../../lib/photo-sources/types';

interface GallerySceneProps {
  items: PhotoItem[];
}

const GAP = 12;

/** Swappable layouts. Promoted from a dev switch to a real control — comparing them is half
 *  the fun of the experiment. */
const EFFECTS = [
  { id: 'editorial', label: 'Editorial' },
  { id: 'depth-masonry', label: 'Masonry' },
  { id: 'flat-grid', label: 'Grid' },
] as const;

type EffectId = (typeof EFFECTS)[number]['id'];

function columnCountFor(width: number): number {
  if (width < 520) return 1;
  if (width < 900) return 2;
  if (width < 1300) return 3;
  return 4;
}

/**
 * Owns layout measurement, effect selection, and the full-screen view.
 * Knows nothing about where the photos came from.
 */
const GalleryScene: React.FC<GallerySceneProps> = ({ items }) => {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const [effect, setEffect] = useState<EffectId>('editorial');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // The editorial layout sorts by date, so the cinematic view must index into *its* order,
  // not the order the source handed us.
  const [ordered, setOrdered] = useState<PhotoItem[]>(items);
  const [order, setOrder] = useState<DateOrder>(DEFAULT_LAYOUT_OPTIONS.order);
  const [grouping, setGrouping] = useState<Grouping>(DEFAULT_LAYOUT_OPTIONS.grouping);
  // Stable identity: this is a `buildLayout` dependency, and a fresh object every render would
  // rebuild the whole layout on every keystroke of scroll state.
  const layoutOptions = useMemo(() => ({ order, grouping }), [order, grouping]);

  const columnCount = columnCountFor(width || 1200);
  const columnWidth = width > 0 ? (width - GAP * (columnCount - 1)) / columnCount : 0;

  const segmented = (active: boolean) =>
    [
      'rounded-md px-2.5 py-1 transition',
      active ? 'bg-white/15 text-white/90' : 'text-white/45 hover:text-white/75',
    ].join(' ');

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px]">
        <div className="flex items-center gap-1">
          <span className="mr-1 hidden text-white/30 sm:inline">layout</span>
          {EFFECTS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setEffect(option.id)}
              className={segmented(effect === option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {effect === 'editorial' && (
          <>
            <div className="flex items-center gap-1">
              <span className="mr-1 hidden text-white/30 sm:inline">order</span>
              <button type="button" onClick={() => setOrder('oldest')} className={segmented(order === 'oldest')}>
                Oldest
              </button>
              <button type="button" onClick={() => setOrder('newest')} className={segmented(order === 'newest')}>
                Newest
              </button>
            </div>
            <div className="flex items-center gap-1">
              <span className="mr-1 hidden text-white/30 sm:inline">chapters</span>
              <button type="button" onClick={() => setGrouping('auto')} className={segmented(grouping === 'auto')}>
                Auto
              </button>
              <button type="button" onClick={() => setGrouping('day')} className={segmented(grouping === 'day')}>
                Day
              </button>
              <button type="button" onClick={() => setGrouping('month')} className={segmented(grouping === 'month')}>
                Month
              </button>
            </div>
          </>
        )}
      </div>

      <div ref={ref} className="w-full">
        {width > 0 &&
          (effect === 'editorial' ? (
            <EditorialSpreads
              items={items}
              width={width}
              options={layoutOptions}
              onOrderedChange={setOrdered}
              onOpen={setOpenIndex}
            />
          ) : (
            <DepthMasonry
              items={items}
              columnWidth={columnWidth}
              columnCount={columnCount}
              gap={GAP}
              flat={effect === 'flat-grid'}
              onOpen={setOpenIndex}
            />
          ))}
      </div>

      {import.meta.env.DEV && <PerfHud tileCount={items.length} />}

      <AnimatePresence>
        {openIndex !== null && (
          <CinematicView
            items={effect === 'editorial' ? ordered : items}
            index={openIndex}
            onIndexChange={setOpenIndex}
            onClose={() => setOpenIndex(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GalleryScene;
